import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import AttachTooltip from 'ember-attacher/components/attach-tooltip';
import BaseRoute from './base-route.gjs';
import Component from '@glimmer/component';
import ConfirmationModal from '../confirmation-modal.gjs';
import ConversationDisplayName from '../conversation-display-name.gjs';
import ConversationMemberListItem from '../conversation-member-list-item.gjs';
import GhostButton from 'ember-simplepractice/components/spds/action/button/ghost';
import NegativeButton from 'ember-simplepractice/components/spds/action/button/negative';
import styles from './conversation-details.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

export default class MessagingRoutesConversationDetails extends Component {
  @service messaging;
  @service messagingNavigation;

  @tracked showLeaveConfirm = false;

  get routeHandler() {
    return this.args.handler;
  }

  get conversation() {
    return this.routeHandler.conversation;
  }

  get isDraft() {
    return this.routeHandler.isDraft;
  }

  get displayName() {
    return this.conversation.displayName;
  }

  get isClinician() {
    return this.messaging.currentProfile.isClinician;
  }

  get showModifyMembers() {
    return this.isDraft;
  }

  get members() {
    return this.routeHandler.members;
  }

  get showViewPreviousMembers() {
    return this.messaging.currentProfile?.isClinician && this.routeHandler.hasPreviousMembers;
  }

  onNameClickFor = member => {
    if (!this.routeHandler.canNavigateToClientProfile(member)) {
      return undefined;
    }

    return () => {
      document.activeElement.blur();
      this.routeHandler.navigateToClientProfile.perform(member);
    };
  };

  goBack = async () => {
    await this.messagingNavigation.leaveChildRoute();
  };

  viewPreviousMembers = async () => {
    await this.messagingNavigation.toPreviousMembers();
  };

  onModifyMembers = () => {};

  openLeaveConfirm = () => {
    if (this.routeHandler.isLeaveDisabled) return;
    this.showLeaveConfirm = true;
  };

  closeLeaveConfirm = () => {
    this.showLeaveConfirm = false;
  };

  leaveConversation = async () => {
    await this.routeHandler.leaveConversation();
  };

  <template>
    {{#if this.showLeaveConfirm}}
      <ConfirmationModal
        @title={{t "emc.conversation_details.leave_confirm_question"}}
        @body={{t "emc.conversation_details.leave_confirm_body"}}
        @confirmLabel={{t "emc.conversation_details.leave_conversation"}}
        @onCancel={{this.closeLeaveConfirm}}
        @onConfirm={{this.leaveConversation}}
      />
    {{else}}
      <BaseRoute
        class="{{styles.component}} conversation-details-page"
        data-test-conversation-details-route
      >
        <:header>
          <GhostButton
            @fitContent={{true}}
            @iconOnly={{true}}
            {{on "click" this.goBack}}
            class="back-button"
            data-test-conversation-details-page-back-button
          >
            {{svgJar "messaging-chevron-left"}}
          </GhostButton>

          <span class="title">{{t "emc.conversation_details.title"}}</span>
        </:header>
        <:default>
          <ConversationDisplayName
            @displayName={{this.displayName}}
            @editable={{this.isClinician}}
            @onSave={{this.routeHandler.updateDisplayName}}
          />

          <div class="members-section-header" data-test-members-section-header>
            <h2 class="section-title" data-test-members-section-title>
              {{t "emc.conversation_details.members_section" count=this.members.length}}
            </h2>

            {{#if this.showModifyMembers}}
              <GhostButton
                @size="small"
                class="modify-members-button"
                {{on "click" this.onModifyMembers}}
                data-test-modify-members
              >
                {{svgJar "messaging-users"}}
                {{t "emc.draft_conversation_banner.modify_members"}}
              </GhostButton>
            {{/if}}
          </div>

          <ul class="members-list" data-test-members-list role="list">
            {{#each this.members key="id" as |member|}}
              <ConversationMemberListItem
                @member={{member}}
                @onNameClick={{this.onNameClickFor member}}
              />
            {{/each}}

            {{#if this.showViewPreviousMembers}}
              <li class="view-previous-members">
                <button
                  type="button"
                  data-test-view-previous-members
                  {{on "click" this.viewPreviousMembers}}
                >
                  <span class="view-previous-members-label">
                    {{t "emc.conversation_details.view_previous_members"}}
                  </span>
                  <span class="view-previous-members-chevron" aria-hidden="true">
                    {{svgJar "messaging-chevron-right"}}
                  </span>
                </button>
              </li>
            {{/if}}
          </ul>

          {{#if this.routeHandler.showLeaveButton}}
            <div class="leave-button-container" data-test-leave-conversation-container>
              <div class="leave-button-wrapper">
                <NegativeButton
                  @fitContentWidth={{true}}
                  @disabled={{this.routeHandler.isLeaveDisabled}}
                  {{on "click" this.openLeaveConfirm}}
                  class="leave-conversation-button"
                  data-test-leave-conversation-button
                >
                  {{t "emc.conversation_details.leave_conversation"}}
                </NegativeButton>
                {{#if this.routeHandler.isLeaveDisabled}}
                  <AttachTooltip
                    @placement="bottom"
                    @arrow={{true}}
                    @animation="fade"
                    @class="emc-tooltip leave-disabled-tooltip"
                    @renderInPlace={{true}}
                  >
                    {{t "emc.conversation_details.leave_conversation_disabled_tooltip"}}
                  </AttachTooltip>
                {{/if}}
              </div>
            </div>
          {{/if}}
        </:default>
      </BaseRoute>
    {{/if}}
  </template>
}
